import { Fragment, useEffect, useState } from 'react';
import { Dropdown, Header, Icon, Label, Message, Table } from 'semantic-ui-react';
import ConfirmationModal from '../../../../../components/confirmationModal';
import { RESPONSE_STATUS } from '../../../../../constnats/general';
import useFullModalState from '../../../../../hooks/useFullModalState';
import { CONFIRM_DELETE_QUALITY_REQUIREMENTS_POLICY } from '../../../../../messages';
import {
    QualityRequirementsPolicyType,
    SaveQualityRequirementsPolicyType,
} from '../../../../../schemas/qualityRequirementsPolicy.schemas';
import Alert from '../../../../../utils/alert';
import QualityRequirementsPoliciesRepo from '../../../../../utils/repository/qualityRequirementsPoliciesRepo';
import QualityRequirementsPolicyModal from '../qualityRequirementsPolicyModal';
import Loader from '../../../../../components/loader';
import { serverToLocalDate } from '../../../../../utils/dateTimeUtils';

function QualityRequirementsPolicies() {
    const policyModal = useFullModalState<QualityRequirementsPolicyType | undefined>();
    const deleteModalState = useFullModalState<QualityRequirementsPolicyType>();
    const [policies, setPolicies] = useState<QualityRequirementsPolicyType[]>([]);
    const [loading, setLoading] = useState(false);

    const loadPolicies = () => {
        setLoading(true);
        QualityRequirementsPoliciesRepo.list(
            (response) => {
                if (response.status === RESPONSE_STATUS.SUCCESS) {
                    setPolicies(response.data.sort((a, b) => a.tableName.localeCompare(b.tableName)));
                    setLoading(false);
                } else {
                    Alert.warn(response.message);
                }
            },
            (error) => Alert.warn(JSON.stringify(error)),
        );
    };

    const createPolicy = (spec: SaveQualityRequirementsPolicyType) => {
        QualityRequirementsPoliciesRepo.create(
            spec,
            (response) => {
                if (response.status === RESPONSE_STATUS.SUCCESS) {
                    Alert.success('Creation successful!');
                    policyModal.closeModal();
                    loadPolicies();
                } else {
                    Alert.warn(response.message);
                }
            },
            (error) => Alert.warn(JSON.stringify(error)),
        );
    };

    const updatePolicy = (id: number, spec: SaveQualityRequirementsPolicyType) => {
        QualityRequirementsPoliciesRepo.update(
            id,
            spec,
            (response) => {
                if (response.status === RESPONSE_STATUS.SUCCESS) {
                    Alert.success('Update successful!');
                    policyModal.closeModal();
                    loadPolicies();
                } else {
                    Alert.warn(response.message);
                }
            },
            (error) => Alert.warn(JSON.stringify(error)),
        );
    };

    const deletePolicy = (policy: QualityRequirementsPolicyType) => {
        QualityRequirementsPoliciesRepo.delete(
            policy.id,
            (response) => {
                if (response.status === RESPONSE_STATUS.SUCCESS) {
                    Alert.success('Deletion successful!');
                    deleteModalState.closeModal();
                    loadPolicies();
                } else {
                    Alert.warn(response.message);
                }
            },
            (error) => Alert.warn(JSON.stringify(error)),
        );
    };

    useEffect(() => {
        loadPolicies();
    }, []);

    return (
        <div>
            <Header as="h2" textAlign="center">
                Quality Requirements Policies
                <Label onClick={() => policyModal.openModal(undefined)} className="cursorPointer">
                    <Icon name="add" />
                    Add
                </Label>
            </Header>
            {loading ? (
                <Loader />
            ) : (
                <Fragment>
                    {policies.length ? (
                        <Table striped>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell></Table.HeaderCell>
                                    <Table.HeaderCell>Id</Table.HeaderCell>
                                    <Table.HeaderCell>Table Name</Table.HeaderCell>
                                    <Table.HeaderCell>Created On</Table.HeaderCell>
                                    <Table.HeaderCell>Updated On</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {policies.map((p) => {
                                    const actions = [
                                        getActionOption('edit', 'Edit', policyModal.openModal),
                                        ...(p.deletable
                                            ? [getActionOption('delete', 'Delete', deleteModalState.openModal)]
                                            : []),
                                    ];
                                    return (
                                        <Table.Row key={p.id}>
                                            <Table.Cell collapsing className="noPadding">
                                                <Dropdown
                                                    name="actions"
                                                    icon="ellipsis vertical"
                                                    options={actions.map((action) => ({
                                                        ...action,
                                                        onClick: () => action.onClick(p),
                                                    }))}
                                                    trigger={<></>}
                                                />
                                            </Table.Cell>
                                            <Table.Cell>{p.id}</Table.Cell>
                                            <Table.Cell>{p.tableName}</Table.Cell>
                                            <Table.Cell>{serverToLocalDate(p.createdOn)}</Table.Cell>
                                            <Table.Cell>{serverToLocalDate(p.updatedOn)}</Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    ) : (
                        <Message>No quality requirements policies to show</Message>
                    )}
                </Fragment>
            )}

            <QualityRequirementsPolicyModal
                open={policyModal.open}
                policy={policyModal.modalData}
                onCancel={policyModal.closeModal}
                onSave={(data) => {
                    if (policyModal.modalData) {
                        updatePolicy(policyModal.modalData.id, data);
                    } else {
                        createPolicy(data);
                    }
                }}
            />
            <ConfirmationModal
                open={deleteModalState.open}
                msg={CONFIRM_DELETE_QUALITY_REQUIREMENTS_POLICY}
                onCancel={deleteModalState.closeModal}
                onConfirm={() => deletePolicy(deleteModalState.modalData!)}
            />
        </div>
    );
}

export default QualityRequirementsPolicies;

function getActionOption<T>(value: string, text: string, onClick: (data: T) => void) {
    return {
        key: value,
        size: 'mini',
        text,
        value,
        onClick,
    };
}
